import type { ReactNode } from 'react';

import type {
  ExperimentKey,
  FlagVariant,
} from '@/shared/services/analytics.types';

import { useExperiment } from './useFlags';

interface ExperimentProps<K extends ExperimentKey> {
  flag: K;
  /** One node per variant key; a missing variant renders `fallback`. */
  variants: Partial<Record<Extract<FlagVariant<K>, string> | 'control', ReactNode>>;
  /** Rendered for `control`, unknown variants, and while loading unless `loading` is given. */
  fallback: ReactNode;
  /** Optional skeleton for the first-visit round trip when the variants differ in layout. */
  loading?: ReactNode;
}

/**
 * Renders the visitor's variant of an experiment. Layout-changing variants
 * should pass `loading`; copy-only variants can render `fallback` (control)
 * until flags arrive without a visible jump.
 */
export function Experiment<K extends ExperimentKey>({
  flag,
  variants,
  fallback,
  loading,
}: ExperimentProps<K>) {
  const { variant, isLoading } = useExperiment(flag);
  if (isLoading) return <>{loading ?? fallback}</>;
  const node = variants[variant as keyof typeof variants];
  return <>{node ?? fallback}</>;
}
